// Typed REST client for the #75 themes surface. Mirrors `api.ts` and
// reuses its `buildHeaders` (JSON + x-grappa-client-id) and `readError`
// (wire-token → `ApiError.code`, plus the shared 401 dead-token handler)
// so these verbs behave identically to the rest of the REST surface —
// with ONE deliberate exception: `getActiveThemePair` is a boot-APPLIED
// cosmetic fetch and opts OUT of the dead-token handler (#502). The rule:
// a boot fetch whose failure mode is "the UI looks slightly wrong" must
// never share the dead-token path with fetches whose failure mode is "the
// session is genuinely gone". See its comment + the `readError` moduledoc.
//
// Keep the wire shapes in lockstep with `lib/grappa/themes/wire.ex`
// (`ThemesWireT`, generated in `wireTypes.ts`) and the token vocabulary
// frozen in `Grappa.Themes.TokenModel` — `TokenPayload` below is the
// typed refinement of `ThemesWireT.payload` (which the server keeps
// `Record<string, unknown>` on the wire).

import { buildHeaders, readError } from "./api";
import { getOrCreateClientId } from "./clientId";
import {
  narrowActiveThemePairOrEmpty,
  narrowActiveThemePairResponse,
  narrowThemeResponse,
  narrowThemesResponse,
} from "./wireNarrow";
import type {
  ThemesWireActivePair,
  ThemesWireBackgroundSize,
  ThemesWireFontFamily,
  ThemesWireT,
} from "./wireTypes";

// The closed font-family allow-list — #1406 X-S9, DERIVED from
// `Grappa.Themes.TokenModel.font_families/0` (the same list `sanitize_font/1`
// guards with) instead of mirrored by hand. `mono-default` maps to the existing
// `--font-mono` stack (no font file); the rest each get a self-hosted
// `@font-face` (sub-task 8, deferred).
export type ThemeFontFamily = ThemesWireFontFamily;

// The 27 color keys — still a HAND mirror of
// `Grappa.Themes.TokenModel.color_keys/0`, and the one vocabulary #1406 X-S9
// leaves unpinned: `nick_${number}` is a pattern template literal that TS
// degrades to an index signature, so narrowing it to the server's exact 16
// nick slots is a behaviour change (a payload missing `nick_5` type-checks
// today and would stop) with real consumer fallout, not a re-export. Declared
// debt, tracked on #1406, deliberately out of this slice.
// Each value is a strict `#rrggbb` string (validated server-side; cic
// treats them as opaque CSS color literals via `customTheme.ts`).
export type ThemeColorKey =
  | "bg"
  | "bg_alt"
  | "fg"
  | "accent"
  | "muted"
  | "border"
  | "mention"
  | "mode_op"
  | "mode_halfop"
  | "mode_voiced"
  | "mode_plain"
  | `nick_${number}`;

export type TokenColors = Record<ThemeColorKey, string>;

// The frozen token vocabulary — the sanitized payload every theme carries.
// Producers can only express what this allows; anything else is dropped
// server-side (safe-by-construction). `customTheme.ts` consumes this to
// generate scoped CSS custom properties.
// Background sizing — #1406 X-S9, derived from
// `Grappa.Themes.TokenModel.size_modes/0`. `cover` = full-bleed (the v1
// built-in set + every upload); `repeat` = seamless tile (the deferred #294
// pattern set).
export type ThemeBackgroundSize = ThemesWireBackgroundSize;

export type TokenPayload = {
  colors: TokenColors;
  font_family: ThemeFontFamily;
  background: {
    // Uploads slug of the re-hosted background image, or null for none.
    image_id: string | null;
    // #294 — a member of the server-owned BuiltinBackgrounds catalog, or null.
    // Mutually exclusive with image_id (a background is EITHER an upload OR a
    // built-in). Resolves to /backgrounds/<builtin>.webp in customTheme.
    builtin: string | null;
    // #294 — cover (v1 default) or repeat (deferred tile mode).
    size: ThemeBackgroundSize;
    // 0.0..1.0; default 0.3.
    opacity: number;
  };
};

// One entry in the built-in background catalog (`GET /themes/backgrounds`) —
// still a HAND mirror of `Grappa.Themes.BuiltinBackgrounds.t`, and the second
// vocabulary #1406 X-S9 leaves unpinned. Not for lack of trying: the
// re-export was written and MEASURED, and the codegen cannot render that type
// — `t/0`'s `variant: variant()` is a same-module ref the external-type path
// has no registry for, so the generated files came out with `variant: Variant`
// (TS2304, a name nothing declares) and a `THEMES_BUILTIN_BACKGROUNDS_VARIANT`
// import (TS2724, a const neither emitter declares). The fix belongs to the
// emitter; restating the four fields server-side to route around it would have
// been the same twin this slice exists to delete. The picker consumes this;
// `path` is the static /backgrounds/<key>.webp URL the asset is served at.
export type BuiltinBackground = {
  key: string;
  name: string;
  variant: "dark" | "light";
  path: string;
};

// Request body for create — atom-keyed at the server boundary; the wire
// `payload` value stays string-keyed (`"colors"` etc.), which
// `TokenPayload` already is.
export type CreateThemeBody = { name: string; payload: TokenPayload };

// Partial edit — name and/or payload. Omitted fields are left untouched
// server-side.
export type UpdateThemeBody = { name?: string; payload?: TokenPayload };

// Background source — an uploaded File (multipart) OR a URL the server
// fetches (SSRF-guarded). One pipeline, two entry shapes.
export type BackgroundSource = { file: File } | { url: string };

export async function listGallery(token: string): Promise<ThemesWireT[]> {
  const res = await fetch("/themes", { headers: buildHeaders(token) });
  if (!res.ok) throw await readError(res);
  return narrowThemesResponse(await res.json()).themes;
}

export async function listMine(token: string): Promise<ThemesWireT[]> {
  const res = await fetch("/me/themes", { headers: buildHeaders(token) });
  if (!res.ok) throw await readError(res);
  return narrowThemesResponse(await res.json()).themes;
}

// GET /themes/unpublished — admin-only view of UNPUBLISHED system built-ins
// (stranded by an unpublish, no owner UI can see them), so an admin can
// re-publish them. Returns [] for non-admins (server-decided authz). #299.
export async function listUnpublishedBuiltins(token: string): Promise<ThemesWireT[]> {
  const res = await fetch("/themes/unpublished", { headers: buildHeaders(token) });
  if (!res.ok) throw await readError(res);
  return narrowThemesResponse(await res.json()).themes;
}

// GET /themes/backgrounds — the server-owned built-in background catalog the
// picker renders. Server-owned so cic never hard-codes the closed set (it would
// drift from the sanitizer's allowlist).
export async function listBuiltinBackgrounds(token: string): Promise<BuiltinBackground[]> {
  const res = await fetch("/themes/backgrounds", { headers: buildHeaders(token) });
  if (!res.ok) throw await readError(res);
  return ((await res.json()) as { backgrounds: BuiltinBackground[] }).backgrounds;
}

export async function getTheme(token: string, id: number): Promise<ThemesWireT> {
  const res = await fetch(`/themes/${id}`, { headers: buildHeaders(token) });
  if (!res.ok) throw await readError(res);
  return narrowThemeResponse(await res.json());
}

export async function createTheme(token: string, body: CreateThemeBody): Promise<ThemesWireT> {
  const res = await fetch("/themes", {
    method: "POST",
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readError(res);
  return narrowThemeResponse(await res.json());
}

export async function updateTheme(
  token: string,
  id: number,
  body: UpdateThemeBody,
): Promise<ThemesWireT> {
  const res = await fetch(`/themes/${id}`, {
    method: "PATCH",
    headers: buildHeaders(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readError(res);
  return narrowThemeResponse(await res.json());
}

export async function deleteTheme(token: string, id: number): Promise<void> {
  const res = await fetch(`/themes/${id}`, {
    method: "DELETE",
    headers: buildHeaders(token),
  });
  if (!res.ok) throw await readError(res);
}

export async function publishTheme(token: string, id: number): Promise<ThemesWireT> {
  const res = await fetch(`/themes/${id}/publish`, {
    method: "POST",
    headers: buildHeaders(token),
  });
  if (!res.ok) throw await readError(res);
  return narrowThemeResponse(await res.json());
}

export async function unpublishTheme(token: string, id: number): Promise<ThemesWireT> {
  const res = await fetch(`/themes/${id}/unpublish`, {
    method: "POST",
    headers: buildHeaders(token),
  });
  if (!res.ok) throw await readError(res);
  return narrowThemeResponse(await res.json());
}

export async function copyTheme(token: string, id: number): Promise<ThemesWireT> {
  const res = await fetch(`/themes/${id}/copy`, {
    method: "POST",
    headers: buildHeaders(token),
  });
  if (!res.ok) throw await readError(res);
  return narrowThemeResponse(await res.json());
}

// #358 — the resolved day/night theme pair. `light` is the day (light-mode)
// slot, `dark` the optional night (dark-mode) slot. A null slot means unset
// (or a dangling pointer); a null `dark` resolves to the light theme in both
// modes (the #75 single pick). cic picks which slot to paint from the OS
// `prefers-color-scheme` signal — never the server.
export type ActiveThemePair = ThemesWireActivePair;

// GET /me/theme — the resolved pair. A bare `null` body still resolves to an
// empty pair so callers never branch on it, but that tolerance now lives in
// `narrowActiveThemePairOrEmpty` and is documented there as the one shape this
// door does not validate. An unset pair is `{light: null, dark: null}` on the
// wire, not `null` — the server has no path that emits the bare value.
//
// #502 — ISOLATED from the dead-token handler (`fireDeadTokenHandler: false`):
// this is a boot-APPLIED cosmetic fetch (`customTheme.mountCustomThemeSync`
// fires it on EVERY token presence — login, reload, cold boot), so a transient
// 401 must NOT clear a valid session's token. Doing so also defeated the
// caller's own tolerant `.catch`, which is written to keep the FOUC-free boot
// cache on a transient failure. It still throws the decoded `ApiError` so that
// catch runs. Mirrors the #449 display-prefs isolation; contrast the on-demand
// theme verbs above/below, which KEEP the handler — a 401 while the user is
// actively managing themes genuinely means the session is dead.
export async function getActiveThemePair(token: string): Promise<ActiveThemePair> {
  const res = await fetch("/me/theme", { headers: buildHeaders(token) });
  if (!res.ok) throw await readError(res, false);
  return narrowActiveThemePairOrEmpty(await res.json());
}

// PUT /me/theme — set the full desired pair. `light` is required; `dark` null
// (or equal to light) is a single pick. The server validates BOTH ids before
// either persists (unknown id → 404 not_found, no half-applied pair).
export async function setActiveThemePair(
  token: string,
  light: number,
  dark: number | null,
): Promise<ActiveThemePair> {
  const res = await fetch("/me/theme", {
    method: "PUT",
    headers: buildHeaders(token),
    body: JSON.stringify({ light, dark }),
  });
  if (!res.ok) throw await readError(res);
  return narrowActiveThemePairResponse(await res.json());
}

// POST /themes/background — upload a File (multipart "file") OR ask the
// server to fetch a URL. Returns the re-hosted uploads slug.
//
// The multipart path must NOT set a JSON content-type: the browser sets
// the `multipart/form-data; boundary=…` header itself when the body is a
// FormData, and forcing application/json here corrupts the request. So it
// builds headers inline (auth + client-id) instead of `buildHeaders`.
export async function uploadBackground(
  token: string,
  source: BackgroundSource,
): Promise<{ image_id: string }> {
  let init: RequestInit;
  if ("file" in source) {
    const form = new FormData();
    form.append("file", source.file);
    init = {
      method: "POST",
      headers: {
        authorization: `Bearer ${token}`,
        "x-grappa-client-id": getOrCreateClientId(),
      },
      body: form,
    };
  } else {
    init = {
      method: "POST",
      headers: buildHeaders(token),
      body: JSON.stringify({ url: source.url }),
    };
  }
  const res = await fetch("/themes/background", init);
  if (!res.ok) throw await readError(res);
  return (await res.json()) as { image_id: string };
}
